import { formatDateRange } from "@itandi/tools/vanilla";
import { z } from "zod";
import {
	TYPE_CLEANING,
	TYPE_MISC_CONSTRUCTION,
	TYPE_RECOVERING_CONSTRUCTION
} from "~/constants/DealConstructionConst";
import type { Deal } from "~/models/Deal";

export type OldDealConstructionSchedule = {
	constructionScheduleType: number;
	endDate: Date;
	id: number;
	startDate: Date;
};

export type ConstructionScheduleType =
	| typeof TYPE_MISC_CONSTRUCTION
	| typeof TYPE_RECOVERING_CONSTRUCTION
	| typeof TYPE_CLEANING;

export type ScheduleStatus = "scheduled" | "adjusting" | "none";

type DealConstructionScheduleBase = {
	constructionScheduleType: ConstructionScheduleType;
	id: number;
};

export type DealConstructionSchedule = DealConstructionScheduleBase &
	(
		| {
				constructionScheduleState: "scheduled";
				endDate: string;
				startDate: string;
		  }
		| {
				constructionScheduleState: "adjusting" | "none";
				endDate: null;
				startDate: null;
		  }
	);

export const getScheduleDisplay = (
	schedule: DealConstructionSchedule | undefined
) => {
	if (schedule === undefined) {
		return {
			state: "unregistered",
			text: "未入力"
		};
	}

	const displays = {
		adjusting: {
			state: "adjusting",
			text: "調整中"
		},
		none: {
			state: "none",
			text: "施工なし"
		},
		scheduled: {
			state: "scheduled",
			text: formatDateRange(schedule.startDate, schedule.endDate)
		}
	} as const satisfies Record<
		ScheduleStatus,
		{ state: ScheduleStatus; text: string }
	>;

	return displays[schedule.constructionScheduleState];
};

const dealConstructionScheduleEditSchema = z
	.object({
		constructionScheduleState: z.enum(["scheduled", "adjusting", "none"]),
		constructionScheduleType: z.union([
			z.literal(TYPE_MISC_CONSTRUCTION),
			z.literal(TYPE_RECOVERING_CONSTRUCTION),
			z.literal(TYPE_CLEANING)
		]),
		endDate: z.string().nullable(),
		startDate: z.string().nullable()
	})
	.refine(
		(schedule) => {
			if (schedule.constructionScheduleState !== "scheduled") {
				return true;
			}
			return schedule.startDate !== null && schedule.endDate !== null;
		},
		{
			message: "開始日と終了日を入力してください",
			path: ["startDate"]
		}
	);

export type DealConstructionScheduleEdit = z.input<
	typeof dealConstructionScheduleEditSchema
>;

export const scheduleInputFormSchema = z.object({
	schedules: z.array(dealConstructionScheduleEditSchema)
});

export type ScheduleInputFormValues = z.input<typeof scheduleInputFormSchema>;

export const CONSTRUCTION_SCHEDULE_LABELS = {
	[TYPE_MISC_CONSTRUCTION]: "雑工事",
	[TYPE_RECOVERING_CONSTRUCTION]: "クロス工事",
	[TYPE_CLEANING]: "クリーニング"
};

export const CONSTRUCTION_NUMBERS: ConstructionScheduleType[] = [
	TYPE_MISC_CONSTRUCTION,
	TYPE_RECOVERING_CONSTRUCTION,
	TYPE_CLEANING
];

export const buildInitialSchedules = (deal: Deal) =>
	CONSTRUCTION_NUMBERS.map((type): DealConstructionScheduleEdit => {
		const schedule = deal.dealConstructionSchedules.find(
			(s) => s.constructionScheduleType === type
		);

		return schedule
			? {
					constructionScheduleState: schedule.constructionScheduleState,
					constructionScheduleType: schedule.constructionScheduleType,
					endDate: schedule.endDate,
					startDate: schedule.startDate
				}
			: {
					constructionScheduleState: "scheduled",
					constructionScheduleType: type,
					endDate: null,
					startDate: null
				};
	});

/**
 * 完工予定日を算出する
 * - 全工種が「施工なし」→ null（未定）
 * - 「調整中」を含む → null（未定）
 * - それ以外 → scheduled な工種の endDate の最大値
 */
export const calculateConstructionEndDate = (
	schedules: DealConstructionScheduleEdit[]
): string | null => {
	if (schedules.some((s) => s.constructionScheduleState === "adjusting")) {
		return null;
	}

	return schedules.reduce<string | null>((max, s) => {
		if (s.constructionScheduleState === "scheduled" && s.endDate) {
			return !max || s.endDate > max ? s.endDate : max;
		}
		return max;
	}, null);
};
